/**
 * Binding: warm Worker pool. The Rust `PoolPolicy` (`crates/compute-scheduler/src/pool.rs`)
 * decides how many Workers to keep, which one to hand out and when to expire them; this class
 * only holds the `Worker` objects by id and constructs, warms and terminates them as told.
 *
 * Safety rule (explained in the Rust module): only never-used Workers are ever parked. A client
 * that acquired a Worker owns it; the inference and planning clients terminate their Worker on
 * reset, so personal data never returns to the pool. The binding reports each owned Worker's
 * `terminate()` to the policy, which warms no spare while an owned Worker is still alive.
 *
 * Without the Rust package (passthrough), the pool keeps nothing: `acquire` creates a Worker and
 * `prewarm` does nothing, which is the app's behaviour before pooling existed.
 *
 * @module apps/web/src/lib/compute/workerPool
 */

import type { PoolPolicyHandle } from './wasm';

/** Options of a pool. */
export interface WorkerPoolOptions {
  /**
   * Initialize a freshly created Worker (for example post a warm-up message and await the
   * reply). Rejection discards the Worker.
   */
  warm?: (worker: Worker, signal: AbortSignal) => Promise<void>;
  /** Supplies the Rust policy once the package has loaded; undefined means passthrough. */
  policy?: () => PoolPolicyHandle | undefined;
}

/** Per-kind pool of interchangeable Workers created by one factory. */
export class WarmWorkerPool {
  /** Idle Workers by policy id. */
  private readonly idle = new Map<number, Worker>();
  /** Next Worker id. */
  private nextId = 1;
  /** Rust policy, created on first use. */
  private policyHandle?: PoolPolicyHandle;
  /** Expiry timer. */
  private expiryTimer?: ReturnType<typeof setTimeout>;

  /** Create a pool around `factory`. */
  constructor(
    /** Creates one new Worker. */
    private readonly factory: () => Worker,
    /** Warm-up and policy source. */
    private readonly options: WorkerPoolOptions = {}
  ) {}

  /** The Rust policy, if available. */
  private policy(): PoolPolicyHandle | undefined {
    this.policyHandle ??= this.options.policy?.();
    return this.policyHandle;
  }

  /**
   * Take a warm Worker when the policy has one, otherwise create a cold one. The caller owns the
   * Worker until it calls `terminate()`, which also tells the policy that the owner ended (the
   * policy warms no spare while an owned Worker is alive).
   */
  acquire(): Worker {
    const policy = this.policy();
    const id = policy?.acquire() ?? -1;
    const worker = this.idle.get(id) ?? this.factory();
    this.idle.delete(id);
    if (policy) {
      // Binding to the Worker API: there is no "terminated" event, so observe the call itself.
      const terminate = worker.terminate.bind(worker);
      let ended = false;
      worker.terminate = () => {
        terminate();
        if (ended) return;
        ended = true;
        policy.ownerEnded();
      };
    }
    return worker;
  }

  /** Start the warm-ups the policy asks for and resolve when they finish. */
  async prewarm(signal?: AbortSignal): Promise<void> {
    const policy = this.policy();
    if (!policy || !this.options.warm) return;
    const count = policy.beginWarm();
    const runs: Promise<void>[] = [];
    for (let index = 0; index < count; index++) runs.push(this.warmOne(policy, signal));
    await Promise.all(runs);
  }

  /** Terminate every idle Worker. */
  clear(): void {
    for (const id of this.policy()?.clear() ?? []) {
      this.idle.get(id)?.terminate();
      this.idle.delete(id);
    }
  }

  /** Number of idle warm Workers. */
  get idleCount(): number {
    return this.idle.size;
  }

  /** Create, warm and park (or discard) one Worker. */
  private async warmOne(policy: PoolPolicyHandle, signal?: AbortSignal): Promise<void> {
    const id = this.nextId++;
    const worker = this.factory();
    const controller = new AbortController();
    const stop = () => controller.abort(signal?.reason);
    signal?.addEventListener('abort', stop, { once: true });
    let ok = false;
    try {
      if (signal?.aborted) throw signal.reason;
      await this.options.warm!(worker, controller.signal);
      ok = true;
    } finally {
      signal?.removeEventListener('abort', stop);
      if (policy.warmFinished(id, ok)) {
        this.idle.set(id, worker);
        this.armExpiry(policy);
      } else worker.terminate();
    }
  }

  /** Terminate expired idle Workers when the policy's next expiry passes. */
  private armExpiry(policy: PoolPolicyHandle): void {
    if (this.expiryTimer !== undefined) clearTimeout(this.expiryTimer);
    const delay = policy.msUntilExpiry();
    if (delay === undefined) return;
    this.expiryTimer = setTimeout(() => {
      this.expiryTimer = undefined;
      for (const id of policy.expire()) {
        this.idle.get(id)?.terminate();
        this.idle.delete(id);
      }
      this.armExpiry(policy);
    }, delay + 1);
  }
}
